import { lazy, Suspense, type ReactElement } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { ROLE } from '@network-verify/shared';
import ProtectedRoute from './ProtectedRoute';
import RoleRoute from './RoleRoute';
import MainLayout from '../layouts/MainLayout';
import PageLoading from '../components/PageLoading';
import PagePlaceholder from '../components/PagePlaceholder';

// 公开页面
const Login = lazy(() => import('../pages/Login'));
const Register = lazy(() => import('../pages/Register'));
const Forbidden = lazy(() => import('../pages/Forbidden'));
const NotFound = lazy(() => import('../pages/NotFound'));

// 受保护页面
const Dashboard = lazy(() => import('../pages/Dashboard'));
const Profile = lazy(() => import('../pages/Profile'));
const Tutorial = lazy(() => import('../pages/Tutorial'));
const AppsList = lazy(() => import('../pages/AppsList'));
const AppDetail = lazy(() => import('../pages/AppDetail'));
const KeysList = lazy(() => import('../pages/KeysList'));
const GenerateKeys = lazy(() => import('../pages/GenerateKeys'));
const DevicesList = lazy(() => import('../pages/DevicesList'));
const VariablesList = lazy(() => import('../pages/VariablesList'));
const AlgorithmsList = lazy(() => import('../pages/AlgorithmsList'));
const BlacklistList = lazy(() => import('../pages/BlacklistList'));
const VerifyLogs = lazy(() => import('../pages/VerifyLogs'));
const AdminLogs = lazy(() => import('../pages/AdminLogs'));
const AdminUsersList = lazy(() => import('../pages/AdminUsersList'));
const AdminUserDetail = lazy(() => import('../pages/AdminUserDetail'));

/** 包裹 Suspense + PageLoading */
function withSuspense(el: ReactElement): ReactElement {
  return <Suspense fallback={<PageLoading />}>{el}</Suspense>;
}

/** 占位路由快捷构造（仅用于暂未实现的页面） */
function placeholder(title: string, description?: string): ReactElement {
  return withSuspense(<PagePlaceholder title={title} description={description} />);
}

export const router = createBrowserRouter([
  // 公开路由
  { path: '/login', element: withSuspense(<Login />) },
  { path: '/register', element: withSuspense(<Register />) },
  { path: '/403', element: withSuspense(<Forbidden />) },

  // 受保护路由（带主布局）
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <MainLayout />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },
      { path: 'dashboard', element: withSuspense(<Dashboard />) },
      { path: 'profile', element: withSuspense(<Profile />) },
      { path: 'tutorial', element: withSuspense(<Tutorial />) },

      // 应用管理
      { path: 'apps', element: withSuspense(<AppsList />) },
      { path: 'apps/:id', element: withSuspense(<AppDetail />) },
      { path: 'apps/:id/api', element: placeholder('WebApi 接口文档') },
      { path: 'apps/:id/variables', element: withSuspense(<VariablesList />) },
      { path: 'apps/:id/algorithms', element: withSuspense(<AlgorithmsList />) },
      { path: 'apps/:id/blacklist', element: withSuspense(<BlacklistList />) },
      { path: 'apps/:id/versions', element: placeholder('版本管理') },

      // 卡密管理
      { path: 'apps/:id/keys', element: withSuspense(<KeysList />) },
      { path: 'apps/:id/keys/generate', element: withSuspense(<GenerateKeys />) },
      { path: 'apps/:id/keys/query', element: placeholder('激活 / 制卡查询') },

      // 设备与在线
      { path: 'apps/:id/devices', element: withSuspense(<DevicesList />) },
      { path: 'apps/:id/online', element: withSuspense(<DevicesList />) },

      // 日志
      { path: 'apps/:id/logs', element: withSuspense(<VerifyLogs />) },
      { path: 'logs', element: withSuspense(<AdminLogs />) },

      // 管理员
      {
        path: 'admin/users',
        element: (
          <RoleRoute roles={[ROLE.ADMIN]}>
            {withSuspense(<AdminUsersList />)}
          </RoleRoute>
        ),
      },
      {
        path: 'admin/users/:id',
        element: (
          <RoleRoute roles={[ROLE.ADMIN]}>
            {withSuspense(<AdminUserDetail />)}
          </RoleRoute>
        ),
      },
    ],
  },

  // 404
  { path: '*', element: withSuspense(<NotFound />) },
]);
